/* Northstar Axiomi — client state + API + small helpers */
const State = {
  user: null, tenant: null, plugins: [], permissions: [], meta: {},
  sites: [],
  // Organisation ↔ site scope (corporate whole-estate vs one site). '' = organisation-wide.
  scope: (() => { try { return localStorage.getItem('esos_scope') || ''; } catch (_) { return ''; } })(),
  setScope(id) { this.scope = id || ''; try { id ? localStorage.setItem('esos_scope', id) : localStorage.removeItem('esos_scope'); } catch (_) {} },
  // Append the active site as a query param for scope-aware endpoints.
  siteParam(prefix) { if (!this.scope) return ''; return (prefix || '?') + 'site=' + encodeURIComponent(this.scope); },
  siteName() { const s = (this.sites || []).find(x => x.id === this.scope); return s ? s.name : 'Organisation-wide'; },
  hasPlugin(name) { return this.plugins.some(p => p.name === name); },
  can(perm) {
    if (!this.permissions) return false;
    if (this.permissions.includes('*')) return true;
    if (this.permissions.includes(perm)) return true;
    const res = perm.split(':')[0];
    return this.permissions.includes(res + ':*');
  }
};

function readCookie(name) {
  const m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
  return m ? decodeURIComponent(m[1]) : '';
}
async function http(method, path, body, opts) {
  opts = opts || {};
  const outbox = (typeof window !== 'undefined') ? window.Outbox : null;
  const queueable = outbox && !opts._replay && outbox.isQueueable(method, path);
  // Offline field create → queue it now instead of failing (drained on reconnect).
  if (queueable && typeof navigator !== 'undefined' && navigator.onLine === false) {
    const entry = await outbox.enqueue(method, path, body);
    return { queued: true, offline: true, opId: entry.opId };
  }
  const headers = body ? { 'Content-Type': 'application/json' } : {};
  // Send the double-submit CSRF token on state-changing requests (no-op if disabled server-side).
  if (method !== 'GET') { const csrf = readCookie('esos_csrf'); if (csrf) headers['X-CSRF-Token'] = csrf; }
  // On replay from the outbox, carry the op's idempotency key so the server dedups.
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  let res;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (netErr) {
    // Network failure (dropped mid-request). Queue field creates; otherwise rethrow.
    if (queueable) {
      const entry = await outbox.enqueue(method, path, body);
      return { queued: true, offline: true, opId: entry.opId };
    }
    throw netErr;
  }
  let data = null;
  try { data = await res.json(); } catch (_) { data = {}; }
  if (!res.ok) {
    // Trial lapsed or subscription past-due: a write was blocked by the server — surface the paywall globally.
    if (res.status === 402 && data && (data.code === 'trial_expired' || data.code === 'subscription_past_due') && typeof window.onTrialExpired === 'function') {
      try { window.onTrialExpired(data.code); } catch (_) {}
    }
    const e = new Error(data.error || res.statusText); e.status = res.status; e.data = data; throw e;
  }
  return data;
}
// Stale-view guard: a GET started while a route's view is loading belongs to that navigation.
// If the user navigates on before it resolves, the stale view must not render over the new
// page (slow first loads on small instances made this visible) — so its GET never settles.
// Background calls (outside a view load) are unaffected. window.__viewSeq is set by router().
function guardedGet(p) {
  const seq = (typeof window !== 'undefined') ? window.__viewSeq : null;
  const pr = http('GET', p);
  if (seq == null) return pr;
  return pr.then((d) => (window.__navSeq !== seq ? new Promise(() => {}) : d), (e) => { if (window.__navSeq !== seq) return new Promise(() => {}); throw e; });
}
const api = {
  get: (p) => guardedGet(p),
  post: (p, b) => http('POST', p, b),
  patch: (p, b) => http('PATCH', p, b),
  put: (p, b) => http('PUT', p, b), // 21 Aug: settings/ai + settings/storage (BYO key/bucket) use PUT semantics
  del: (p) => http('DELETE', p),
  // P-DOC: raw-binary upload. Sends file bytes with metadata in the query string.
  async upload(file, meta = {}) {
    const qs = new URLSearchParams(Object.assign({ name: file.name || 'file' }, meta)).toString();
    const headers = { 'Content-Type': file.type || 'application/octet-stream' };
    const csrf = readCookie('esos_csrf'); if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch('/api/files/upload?' + qs, { method: 'POST', headers, credentials: 'same-origin', body: file });
    let data = null; try { data = await res.json(); } catch (_) { data = {}; }
    if (!res.ok) { const e = new Error(data.error || res.statusText); e.status = res.status; throw e; }
    return data;
  },
  fileUrl: (id) => '/api/files/' + id + '/download',
  // Axiomi AI smart-fill: propose form field values from a plain-language description.
  smartFill: (entity, description, fields) => http('POST', '/api/genai/smart-fill', { entity, description, fields }),
  // Outbox introspection for the UI (pending count, manual flush).
  outbox: {
    pending: () => (typeof window !== 'undefined' && window.Outbox && window.Outbox.pendingCount) ? window.Outbox.pendingCount() : Promise.resolve(0),
    flush: () => (typeof window !== 'undefined' && window.Outbox && window.Outbox.flush) ? window.Outbox.flush() : Promise.resolve(),
  },
};

// Wire the offline outbox to this HTTP sender and begin draining on reconnect.
if (typeof window !== 'undefined' && window.Outbox && window.Outbox._bindHttp) {
  window.Outbox._bindHttp((m, p, b, o) => http(m, p, b, o));
  window.Outbox.start();
}

/* ---- small formatting helpers ---- */
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function fmtDate(s) { if (!s) return '—'; const d = new Date(s); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtDateTime(s) { if (!s) return '—'; const d = new Date(s); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
function relTime(s) {
  if (!s) return '';
  const diff = Date.now() - new Date(s).getTime(); const day = 86400000;
  if (diff < 3600000) return Math.max(1, Math.round(diff / 60000)) + 'm ago';
  if (diff < day) return Math.round(diff / 3600000) + 'h ago';
  if (diff < 7 * day) return Math.round(diff / day) + 'd ago';
  return fmtDate(s);
}
function titleCase(s) { return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }
/* Axiomi guidance markers — (i) "what this is" and (?) "what to do". Hover/focus for a tip. */
function _axAttr(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
function help(text) { const s = _axAttr(text); return `<span class="ax-help" data-tip="${s}" title="${s}" tabindex="0" role="note" aria-label="Info: ${s}">i</span>`; }
function helpQ(text) { const s = _axAttr(text); return `<span class="ax-help q" data-tip="${s}" title="${s}" tabindex="0" role="note" aria-label="Help: ${s}">?</span>`; }
(function injectAxHelpStyle() {
  if (typeof document === 'undefined' || document.getElementById('ax-help-style')) return;
  const st = document.createElement('style'); st.id = 'ax-help-style';
  st.textContent = '.ax-help{display:inline-flex;align-items:center;justify-content:center;width:15px;height:15px;border-radius:50%;background:var(--grey-bg,#e7ebf0);color:var(--muted,#64748b);font-size:10px;font-style:normal;font-weight:700;cursor:help;margin-left:5px;vertical-align:middle;position:relative;font-family:Georgia,\'Times New Roman\',serif}.ax-help.q{font-family:inherit}.ax-help:hover,.ax-help:focus{background:var(--green-600,#0e9c74);color:#fff;outline:none}.ax-help:hover::after,.ax-help:focus::after{content:attr(data-tip);position:absolute;bottom:135%;left:50%;transform:translateX(-50%);background:#0b1220;color:#fff;padding:8px 11px;border-radius:8px;font-size:12px;font-weight:400;font-style:normal;line-height:1.45;width:max-content;max-width:280px;z-index:9999;box-shadow:0 8px 28px rgba(0,0,0,.28);white-space:normal;pointer-events:none;text-align:left}';
  document.head.appendChild(st);
})();
function initials(name) { return String(name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase(); }
function daysLeft(s) { if (!s) return null; return Math.round((new Date(s).getTime() - Date.now()) / 86400000); }
function dueLabel(s) {
  const d = daysLeft(s); if (d == null) return '—';
  if (d < 0) return `${-d}d overdue`;
  if (d === 0) return 'due today';
  return `in ${d}d`;
}
